import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LogoFull } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { useAuth } from '@/lib/auth';

export function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const from = (loc.state as { from?: string } | null)?.from ?? '/'; // trang chủ tự chọn: tài xế → /tai-xe, còn lại → /tong-quan

  if (user) return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      nav(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đăng nhập thất bại');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full">
      {/* Bên trái: nhận diện thương hiệu (ẩn trên điện thoại) */}
      <div className="relative hidden flex-1 flex-col items-center justify-center overflow-hidden bg-logo-surface p-10 text-logo-surface-foreground lg:flex">
        <LogoFull className="relative w-full max-w-sm" />
        <div className="relative mt-8 text-center">
          <div className="text-sm font-semibold tracking-wide uppercase">Công ty TNHH XNK Thép & Thiết bị Công nghiệp Minh Tiến</div>
          <div className="mt-1 text-sm text-logo-surface-muted">Thép · Inox · Thiết bị PCCC · Ống nhựa · Vật tư điện nước</div>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex h-1.5">
          <div className="flex-1 bg-brand-green" />
          <div className="flex-1 bg-brand-red" />
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center bg-background p-4 lg:max-w-xl">
        <form onSubmit={submit} className="w-full max-w-sm rounded-xl border bg-card p-7 shadow-lg shadow-primary/5">
          <div className="mx-auto mb-4 w-44 rounded-lg bg-logo-surface p-2 lg:hidden">
            <LogoFull className="w-full" />
          </div>
          <div className="mb-6">
            <h1 className="text-xl font-bold">Đăng nhập</h1>
            <p className="mt-1 text-sm text-muted-foreground">Hệ thống quản lý bán hàng Minh Tiến</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="u">Tên đăng nhập</Label>
              <Input id="u" className="h-10" autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="p">Mật khẩu</Label>
              <Input
                id="p"
                className="h-10"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-sm text-destructive">{error}</div>}
            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy && <Spinner />} Đăng nhập
            </Button>
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">Quên mật khẩu? Liên hệ quản trị hệ thống.</p>
        </form>
      </div>
    </div>
  );
}
